import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AppContext } from '@/lib/core/types';
import { useLocaleStore } from '@/lib/core/stores';
import { useClientTranslation } from '@/lib/core/i18n';
import { formatDate, formatTimeFromDate } from '@/lib/core/utils';
import type { CheckoutContentResponseModel, EventRegistrationDto } from '@/services/platform/api/model';
import { CheckoutContexts } from '@/services/platform/data/checkout';
import type { CheckoutOutcome } from './CheckoutForm';
import { CheckoutScreen } from './CheckoutScreen';
import { forgetCheckout, recallCheckout, rememberCheckout } from './session';
import type {
  CheckoutPurchase,
  EventCheckoutRetryState,
  EventCheckoutState,
  PaidRegistrationState,
} from './types';

interface EventCheckoutScreenProps {
  content: CheckoutContentResponseModel;
  context?: AppContext;
}

/**
 * Adapter for a paid event booking (Figma: Checkout | Payment methods, node 1716:92982 and
 * variants) over the generic `CheckoutScreen`.
 *
 * Reached from the event book flow, which navigates here with the chosen occurrence and guest count
 * in router state once `resolvePaymentDecision` says the event is `paid`. Landing here without that
 * state means a deep link or a refresh — there is nothing to pay for, and `CheckoutScreen` bounces
 * back on its own once it sees an `undefined` purchase.
 */
export function EventCheckoutScreen({ content, context }: EventCheckoutScreenProps) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locale = useLocaleStore(state => state.locale);

  // Router state first, session storage as the fallback. A redirect-based method (MB WAY's hosted
  // authorisation page) and a plain refresh both destroy router state, and by then a PaymentIntent
  // may already exist — so losing the booking here would strand a real payment.
  const routedBooking = (location.state as { eventCheckout?: EventCheckoutState } | null)?.eventCheckout;
  const booking = useMemo(() => routedBooking ?? recallCheckout('event'), [routedBooking]);

  useEffect(() => {
    if (routedBooking) rememberCheckout({ kind: 'event', ...routedBooking });
  }, [routedBooking]);

  const purchase: CheckoutPurchase | undefined = useMemo(() => {
    if (!booking) return undefined;
    const date = formatDate(booking.occurrenceStart, locale);
    const time = formatTimeFromDate(booking.occurrenceStart);
    const when = [date, time].filter(Boolean).join(' · ');

    return {
      context: CheckoutContexts.Event,
      contextReference: booking.eventId,
      amountMajor: (booking.unitPrice ?? 0) * (booking.numberOfGuests ?? 1),
      currencyCode: booking.currencyCode,
      returnPath: booking.returnPath,
      eventBooking: {
        occurrenceId: booking.occurrenceId ?? null,
        numberOfGuests: booking.numberOfGuests,
      },
      summaryRows: [
        ...(booking.venueName ? [{ label: t('App.Checkout.Where'), value: booking.venueName }] : []),
        ...(when ? [{ label: t('App.Checkout.When'), value: when }] : []),
        ...(booking.numberOfGuests != null
          ? [
              {
                label: t('App.Checkout.HowMany'),
                value: t('App.Checkout.Guests', { count: booking.numberOfGuests }),
              },
            ]
          : []),
      ],
    };
  }, [booking, locale, t]);

  const handleSucceeded = useCallback(
    (outcome: { kind: 'succeeded'; reservationId?: string | null }) => {
      if (!booking) return;

      // The server registered the event inside the payment's success handler, so there is no
      // `registerEventV1` call to make — the reservation id from the payment stands in for the
      // registration until the book flow's own fetch can confirm it (see `flows/event/index.tsx`,
      // which prefers the server's `EventDetailDto.myRegistration` over this the moment it is
      // available for the same occurrence).
      //
      // `reservationId` is only ever populated after the registration write succeeds and its Order
      // is linked (`EventPaymentContextHandler.OnSucceededAsync` → `CreateOrderAsync` — verified: the
      // Order, and with it `reservationId`, is created strictly after `RegisterAsync` returns
      // success). Its absence on an otherwise-succeeded payment means that write either failed
      // outright or has not landed yet — `PendingSync` (a real registration status: "not in
      // Salesforce yet, but active") is the honest placeholder for that ambiguity. It renders as
      // "waiting", not a false celebration, and costs no extra request — unlike a wrong guess here,
      // which would need `myRegistration` to catch and correct it.
      const paidRegistration: EventRegistrationDto = {
        id: outcome.reservationId ?? undefined,
        status: outcome.reservationId ? 'Confirmed' : 'PendingSync',
        occurrenceId: booking.occurrenceId ?? undefined,
        numberOfGuests: booking.numberOfGuests,
      };
      const state: PaidRegistrationState = {
        eventId: booking.eventId,
        occurrenceId: booking.occurrenceId,
        numberOfGuests: booking.numberOfGuests,
        paidRegistration,
      };
      // `CheckoutScreen` already forgets the session before calling this — the copy must not
      // survive to be picked up by a later checkout, but that's handled once, generically, there.
      navigate(booking.returnPath, { replace: true, state });
    },
    [booking, navigate]
  );

  /**
   * Refusals the member can fix in the book flow go back there rather than ending on an error:
   * seats still left → "How many" again on the same date; date gone → pick another. Nothing was
   * charged in either case. A sold out with no seats (or an unreadable count) stays on the error.
   */
  const handleOutcome = useCallback(
    (outcome: Exclude<CheckoutOutcome, { kind: 'succeeded' }>): boolean => {
      if (!booking) return false;
      let state: EventCheckoutRetryState | undefined;
      if (outcome.kind === 'eventSoldOut' && outcome.availableSpots != null && outcome.availableSpots > 0) {
        state = {
          eventId: booking.eventId,
          occurrenceId: booking.occurrenceId,
          seatsLeft: outcome.availableSpots,
        };
      } else if (outcome.kind === 'eventDateGone') {
        state = { eventId: booking.eventId, dateGone: true };
      }
      if (!state) return false;
      // The attempt is over — the copy must not be picked up again by the next checkout.
      forgetCheckout();
      navigate(booking.returnPath, { replace: true, state });
      return true;
    },
    [booking, navigate]
  );

  return (
    <div className="pb-safe-area pt-safe-area relative w-full">
      <CheckoutScreen
        content={content}
        context={context}
        purchase={purchase}
        onSucceeded={handleSucceeded}
        onOutcome={handleOutcome}
      />
    </div>
  );
}
